import { Link } from "react-router";
import { ArrowRight, LayoutTemplate } from "lucide-react";
import { Skeleton } from "@/components/skeleton";
import type { PageTemplate } from "@ease/page-builder";

import { buildPickOneCustomizationPath } from "@/hooks/use-vertical-templates";

interface TemplateBrowserProps {
  templates: PageTemplate[];
  isLoading?: boolean;
  error?: string | null;
  siteId?: string | null;
}

function getTemplateSectionTypes(template: PageTemplate) {
  return [...new Set(template.pages.flatMap((page) => page.sectionTypes))].slice(0, 8);
}

export function TemplateBrowser({
  templates,
  isLoading = false,
  error = null,
  siteId,
}: TemplateBrowserProps) {
  if (isLoading) {
    return (
      <div className="grid gap-5 xl:grid-cols-2">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="rounded-[24px] border border-border bg-background p-6 shadow-sm">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="mt-4 h-4 w-full" />
            <Skeleton className="mt-2 h-4 w-5/6" />
            <div className="mt-5 space-y-3">
              <Skeleton className="h-20 w-full rounded-2xl" />
              <Skeleton className="h-20 w-full rounded-2xl" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-[24px] border border-destructive/25 bg-destructive/5 p-6 text-sm text-destructive">
        {error}
      </div>
    );
  }

  return (
    <div className="grid gap-5 xl:grid-cols-2">
      {templates.map((template) => {
        const thumbnail = template.thumbnail ?? {
          eyebrow: template.vertical,
          title: template.pages[0]?.title ?? template.name,
          accent: "from-primary/20 via-primary/5 to-white",
          sections: template.pages[0]?.sectionTypes?.slice(0, 4) ?? [],
        };
        const sectionTypes = getTemplateSectionTypes(template);

        return (
        <article key={template.id} className="rounded-[24px] border border-border bg-background p-6 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                {template.vertical}
              </p>
              <h3 className="mt-2 text-xl font-semibold text-foreground">{template.name}</h3>
            </div>
            <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
              {template.pageCount} starters
            </span>
          </div>

          <p className="mt-3 text-sm leading-6 text-muted-foreground">{template.description}</p>
          {sectionTypes.length > 0 ? (
            <div className="mt-4 rounded-2xl border border-border bg-card p-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                Section library
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {sectionTypes.map((section) => (
                  <span
                    key={`${template.id}-library-${section}`}
                    className="rounded-full bg-background px-2.5 py-1 text-[11px] font-medium text-foreground"
                  >
                    {section}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
          <div className={`mt-3 rounded-2xl border border-border bg-gradient-to-br ${thumbnail.accent} p-4`}>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              {thumbnail.eyebrow}
            </p>
            <p className="mt-2 text-base font-semibold text-foreground">{thumbnail.title}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {thumbnail.sections.slice(0, 4).map((section) => (
                <span
                  key={`${template.id}-${section}`}
                  className="rounded-full border border-border/70 bg-background/80 px-2.5 py-1 text-[11px] font-medium text-foreground"
                >
                  {section}
                </span>
              ))}
            </div>
            <p className="mt-3 text-xs leading-5 text-muted-foreground">
              {template.preview}
            </p>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {template.tags.map((tag) => (
              <span
                key={`${template.id}-${tag}`}
                className="rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground"
              >
                {tag}
              </span>
            ))}
          </div>

          <div className="mt-5 space-y-3">
            {template.pages.map((page) => (
              <div key={`${template.id}-${page.id}`} className="rounded-2xl border border-border bg-card p-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-foreground">{page.title}</p>
                    <p className="text-sm leading-6 text-muted-foreground">{page.description}</p>
                    {page.sectionTypes.length > 0 ? (
                      <p className="text-xs leading-5 text-muted-foreground">
                        {page.sectionTypes.slice(0, 4).join(" · ")}
                      </p>
                    ) : null}
                  </div>
                  <Link
                    to={buildPickOneCustomizationPath({
                      templateVertical: template.vertical,
                      starterPageId: page.id,
                      siteId: siteId ?? undefined,
                    })}
                    className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90"
                  >
                    Customize starter
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </article>
      )})}

      {templates.length === 0 ? (
        <div className="rounded-[24px] border border-dashed border-border bg-background p-8 text-sm leading-6 text-muted-foreground">
          <div className="flex items-center gap-2 text-foreground">
            <LayoutTemplate className="h-4 w-4 text-primary" />
            No templates are available yet.
          </div>
        </div>
      ) : null}
    </div>
  );
}
